import React, { useState } from 'react';
import { Button } from '../ui/button';
import { X, Upload, Image as ImageIcon } from 'lucide-react';

const PhotoAlbumUploader = ({ photos = [], onPhotosChange, maxPhotos = 36 }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length === 0) return;
    
    // Vérifier le nombre total de photos
    const totalPhotos = photos.length + files.length;
    if (totalPhotos > maxPhotos) {
      setError(`Maximum ${maxPhotos} photos autorisées. Vous avez sélectionné ${files.length} photos mais vous avez déjà ${photos.length} photos.`);
      return;
    }
    
    setError('');
    setUploading(true);

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/upload-photo-album`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de l\'upload');
      }

      const data = await response.json();
      console.log('📸 Photos uploadées:', data);
      
      // Ajouter les nouvelles photos à la liste existante
      const updatedPhotos = [...photos, ...data.photos];
      console.log('📸 Album mis à jour:', updatedPhotos);
      onPhotosChange(updatedPhotos);
      
    } catch (err) {
      console.error('Erreur upload:', err);
      setError(err.message || 'Erreur lors de l\'upload des photos');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = (index) => {
    const updatedPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange(updatedPhotos);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Album Photo ({photos.length}/{maxPhotos})
        </label>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
          {error}
        </div>
      )}

      {/* Zone d'upload */}
      {photos.length < maxPhotos && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
          <input
            type="file"
            id="photo-album-upload"
            multiple
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          <label
            htmlFor="photo-album-upload"
            className="cursor-pointer flex flex-col items-center"
          >
            <Upload className="w-12 h-12 text-gray-400 mb-3" />
            <span className="text-sm font-medium text-gray-700">
              {uploading ? 'Upload en cours...' : 'Cliquer pour sélectionner des photos'}
            </span>
            <span className="text-xs text-gray-500 mt-1">
              Maximum {maxPhotos} photos - JPG, PNG, WEBP
            </span>
            <span className="text-xs text-gray-500">
              Encore {maxPhotos - photos.length} photo(s) disponible(s)
            </span>
          </label>
        </div>
      )}

      {/* Grille de photos */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {photos.map((photo, index) => {
            // Construire l'URL complète si nécessaire
            const photoUrl = photo.startsWith('http') 
              ? photo 
              : `${process.env.REACT_APP_BACKEND_URL}${photo}`;
            
            return (
              <div key={index} className="relative group">
                <img
                  src={photoUrl}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                  onError={(e) => {
                    console.error('Erreur chargement image:', photoUrl);
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EErreur%3C/text%3E%3C/svg%3E';
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleRemovePhoto(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs">
                  {index + 1}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {photos.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Aucune photo dans l'album</p>
        </div>
      )}
    </div>
  );
};

export default PhotoAlbumUploader;
